import { useMemo, useCallback, useState, useEffect } from 'react'
import { championshipPlaneIsComplete } from './championshipPlaneEvidence.js'
import { useChampionships } from './useChampionships.js'
import { usePursuits } from './usePursuits.js'
import { adaptPursuitRows } from './pursuitAdapter.js'
import { isPending } from './categories.js'

/**
 * ONE UNIVERSE HOOK for the Command Center's category spaces.
 *
 * It switches on the selected category's `source`, reads the right data plane,
 * and hands back a UNIFORM shape:
 *
 *   { data, status, error, reload, loadedCount, pageCount, complete, demo,
 *     patchLocal, pending, source, config, owners }
 *
 * `data` is ALWAYS a list of normalized championship records. Pursuit rows go
 * through `pursuitAdapter.js`, so PursuitBoard, GapsBoard, ManagerRollup, the
 * map, the filter bar, the row selection and the CSV export consume them with
 * no change at all — the record shape is the contract between the planes and
 * the views.
 *
 * Two deliberate properties:
 *
 *  - Championships stay mounted whichever space is selected, so switching back
 *    to the default space is instant rather than re-paging ~12k records. The
 *    pursuit hook is handed a NULL line (and so fetches nothing) unless a live
 *    pursuit space is selected.
 *  - A `pending-data` space never fetches. There is no line behind it yet and a
 *    guaranteed 404 is not a load: it reports `pending: true` and the page
 *    renders the honest "being fed in" card instead of an empty board.
 */

const NOOP = () => {}

export function useCategoryUniverse(category) {
  const pending = isPending(category)
  const usesPursuits = category?.source === 'pursuits'

  const champ = useChampionships()
  const line = usesPursuits && !pending ? category.line : null
  const pursuits = usePursuits(line)

  /**
   * OPTIMISTIC PATCHES, keyed by the ADAPTED record id.
   *
   * A pursuit row is read-only on load, but the recognition create
   * (`POST /api/recognition-deal`) does write — and the deal it creates only
   * comes back through the association read on the NEXT page load. Without this
   * a rep nominates a hero and the badge still says NO HERO, which reads as a
   * failed create.
   *
   * The patch is applied over the adapted record rather than the raw row, so it
   * speaks the same shape every view already consumes and nothing has to be
   * re-adapted. It carries only what the create actually returned — see
   * `optimisticPatch` in recognition.js, which deliberately invents no stage.
   * Cleared on a line switch and on an explicit reload, at which point the
   * server's own answer is the record again.
   */
  const [patches, setPatches] = useState({})
  useEffect(() => { setPatches({}) }, [line])

  const patchPursuitLocal = useCallback((id, fields) => {
    const key = String(id || '')
    if (!key || !fields) return
    setPatches((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...fields } }))
  }, [])

  const adapted = useMemo(() => {
    if (!usesPursuits || pending) return []
    const rows = adaptPursuitRows(pursuits.rows, {
      config: pursuits.config, owners: pursuits.owners, category,
    })
    const keys = Object.keys(patches)
    if (!keys.length) return rows
    return rows.map((r) => (patches[r.id] ? { ...r, ...patches[r.id] } : r))
  }, [usesPursuits, pending, pursuits.rows, pursuits.config, pursuits.owners, category, patches])

  const reloadPursuits = pursuits.reload
  const reloadChampionships = champ.reload
  const reload = useCallback(() => {
    if (usesPursuits) { setPatches({}); reloadPursuits() }
    else reloadChampionships()
  }, [usesPursuits, reloadPursuits, reloadChampionships])

  /**
   * THE CHAMPIONSHIPS PLANE, EXPOSED ALONGSIDE.
   *
   * `useChampionships` is mounted whichever space is selected (so switching back
   * is instant), which means the whole championship universe is ALREADY in
   * memory in every space at no extra cost. The Cheer space needs it: a cheer
   * gym's championship footprint is its teams' titles, and those titles live on
   * this plane. Handing it over here costs one reference and no request; every
   * other space simply ignores it.
   */
  const championships = champ.data

  if (!usesPursuits) {
    /**
     * A SPACE MAY SCOPE THE CHAMPIONSHIP PLANE WITHOUT BEING A SECOND SOURCE.
     *
     * Bowl Season is the first: the same HubSpot object, a subset of the rows.
     * It is deliberately a FILTER rather than a new data plane, because a second
     * fetch of records already in memory would double the load time to show
     * fewer of them — and, worse, would let the two views drift out of sync on a
     * record they both hold.
     *
     * `championships` below stays the FULL universe on purpose. Cheer's gym
     * rollup reads it to match teams to titles, and handing it a filtered set
     * would silently shrink that footprint.
     */
    const filter = category?.recordFilter
    const data = filter && Array.isArray(champ.data) ? champ.data.filter(filter) : champ.data
    return {
      ...champ,
      data,
      /* The count must describe what the space SHOWS, not what was fetched.
         "13,598 records loaded" over a 42-row board is the kind of number that
         quietly teaches somebody the page is broken. */
      loadedCount: filter ? (data?.length ?? 0) : champ.loadedCount,
      championships,
      championshipsComplete: champ.complete === true,
      championshipsStatus: champ.status,
      championshipsReadHealthy: championshipPlaneIsComplete(champ),
      championshipsRefreshError: champ.asOf?.refreshError || null,
      championshipsReadEvidence: champ.readEvidence,
      reload,
      pending: false,
      source: 'championships',
      config: null,
      owners: {},
    }
  }

  if (pending) {
    return {
      data: [],
      status: 'done',
      error: '',
      reload: NOOP,
      loadedCount: 0,
      pageCount: 0,
      complete: false,
      demo: champ.demo,
      patchLocal: NOOP,
      pending: true,
      source: 'pursuits',
      config: null,
      owners: {},
      championships,
      championshipsComplete: champ.complete === true,
      championshipsStatus: champ.status,
      championshipsReadHealthy: championshipPlaneIsComplete(champ),
      championshipsRefreshError: champ.asOf?.refreshError || null,
      championshipsReadEvidence: champ.readEvidence,
    }
  }

  return {
    data: adapted,
    status: pursuits.status === 'idle' ? 'loading' : pursuits.status,
    error: pursuits.error,
    reload,
    loadedCount: adapted.length,
    pageCount: 0,
    complete: pursuits.status === 'done',
    demo: champ.demo,
    // The ONE write a pursuit space has: the recognition / pursuit create. The
    // load is still read-only — this reflects a deal the backend just created.
    patchLocal: patchPursuitLocal,
    pending: false,
    source: 'pursuits',
    config: pursuits.config,
    owners: pursuits.owners,
    championships,
    championshipsComplete: champ.complete === true,
    championshipsStatus: champ.status,
      championshipsReadHealthy: championshipPlaneIsComplete(champ),
      championshipsRefreshError: champ.asOf?.refreshError || null,
      championshipsReadEvidence: champ.readEvidence,
  }
}
